/**
 * Report Domain Types
 *
 * Radiology reports for DICOM studies
 * Colombian healthcare compliance - signed reports
 */

import type { ReportStatus } from '@/config/constants'

/**
 * Report domain model
 */
export interface Report {
  id: string
  studyId: string
  title: string
  findings: string
  impression: string
  recommendations: string | null
  templateId: string | null
  status: ReportStatus
  createdBy: string
  signedBy: string | null
  signedAt: string | null
  signatureHash: string | null
  signaturePayload: string | null
  amendedFrom: string | null
  amendmentReason: string | null
  version: number
  // BIRADS category for mammography reports (0-6)
  biradsCategory: string | null
  // Transcription workflow
  transcribedBy: string | null
  audioUrl: string | null
  transcriptionNotes: string | null
  // Timestamps
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

/**
 * Create report data
 */
export interface CreateReportData {
  studyId: string
  title: string
  findings: string
  impression: string
  recommendations?: string
  templateId?: string
  biradsCategory?: string
}

/**
 * Update report data (only for DRAFT/PRELIMINARY)
 */
export interface UpdateReportData {
  title?: string
  findings?: string
  impression?: string
  recommendations?: string
}

/**
 * Amend report data (for FINAL reports)
 */
export interface AmendReportData {
  findings: string
  impression: string
  recommendations?: string
  amendmentReason: string
}

/**
 * Report search filters
 */
export interface ReportFilters {
  studyId?: string
  createdBy?: string
  signedBy?: string
  status?: ReportStatus
  dateFrom?: string
  dateTo?: string
  limit?: number
  offset?: number
}

/**
 * Report with patient and study info (for list views)
 */
export interface ReportWithPatient extends Report {
  // Patient info
  patientName: string
  patientDocumentType: string
  patientDocumentNumber: string
  // Study info
  modality: string
  studyDescription: string | null
  studyDate: string
}

/**
 * Report search result
 */
export interface ReportSearchResult {
  reports: ReportWithPatient[]
  total: number
  limit: number
  offset: number
}

/**
 * Valid status transitions
 *
 * Standard workflow: DRAFT -> PRELIMINARY -> FINAL
 * Transcription workflow: DRAFT -> TRANSCRIBING -> REVIEW -> PRELIMINARY -> FINAL
 */
export const VALID_REPORT_STATUS_TRANSITIONS: Record<ReportStatus, ReportStatus[]> = {
  DRAFT: ['PRELIMINARY', 'TRANSCRIBING', 'CANCELLED'],
  TRANSCRIBING: ['REVIEW', 'DRAFT', 'CANCELLED'],
  REVIEW: ['PRELIMINARY', 'TRANSCRIBING', 'CANCELLED'],
  PRELIMINARY: ['DRAFT', 'FINAL', 'CANCELLED'],
  FINAL: ['AMENDED'],
  AMENDED: [],
  CANCELLED: ['DRAFT'],
}

/**
 * Check if report status transition is valid
 */
export const isValidReportStatusTransition = (
  from: ReportStatus,
  to: ReportStatus
): boolean => {
  return VALID_REPORT_STATUS_TRANSITIONS[from].includes(to)
}

/**
 * Check if report can be edited
 */
export const canEditReport = (status: ReportStatus): boolean => {
  return status === 'DRAFT' || status === 'PRELIMINARY' || status === 'TRANSCRIBING'
}

/**
 * Check if report can be transcribed
 */
export const canTranscribeReport = (status: ReportStatus): boolean => {
  return status === 'DRAFT' || status === 'TRANSCRIBING'
}

/**
 * Check if report can be submitted for review
 */
export const canSubmitForReview = (status: ReportStatus): boolean => {
  return status === 'TRANSCRIBING'
}

/**
 * Check if report can be signed
 */
export const canSignReport = (status: ReportStatus): boolean => {
  // DRAFT tambien: es el informe que el propio radiologo acaba de redactar,
  // sin pasar por transcripcion. Quien lo escribio es quien lo firma, asi
  // que no hay nada que revisar antes. Misma regla que en el servidor
  // (backend/src/domain/report/types.ts).
  return status === 'PRELIMINARY' || status === 'DRAFT'
}

/**
 * Check if report can be amended
 */
export const canAmendReport = (status: ReportStatus): boolean => {
  return status === 'FINAL'
}

/**
 * Check if report can be deleted
 */
export const canDeleteReport = (status: ReportStatus): boolean => {
  return status === 'DRAFT'
}
